import type { FieldMap } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import {
	AccessForbiddenError,
	RemoteFieldsNotInitializedError,
	SharedViewAuthenticationMissingError,
	SharedViewFieldsUnavailableError,
} from '@jira/polaris__remote-field/src/SharedViewAuthenticationMissingError.tsx';
import { isShallowEqual } from '@jira/polaris__lib-equals/src/index.tsx';
import {
	jpdProjectPageLoad,
	PAGE_LOAD_MARK_LOAD_FIELDS_START,
	PAGE_LOAD_MARK_LOAD_FIELDS_END,
} from '@jira/polaris__component-metrics/src/jpdProjectPageLoad.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';

// Error message: [...] API-9998 permission check failed [...]
const isPermissionCheckError = (error: Error | unknown): boolean =>
	error instanceof Error ? error.message.search('API-9998') !== -1 : false;

const detectAndHandleCyclicFormulaDependency = (containerProps: Props, fields: FieldMap) => {
	const cyclicFieldLabels = Object.values(fields)
		.map((field) => (field.hasCyclicFormulaDependency ? field.label : undefined))
		.filter(Boolean);
	if (cyclicFieldLabels.length > 0) {
		containerProps.onCyclicFormulaDependencyDetected(cyclicFieldLabels);
	}
};

export const loadFields =
	(forceReload?: boolean, scheduledReload?: boolean): Action<State, Props> =>
	async ({ getState, setState, dispatch }, containerProps) => {
		const { containerAri, isProjectOnboarded, fieldRemote, isSiteAdmin } = containerProps;
		const loadingProps = { containerAri, isProjectOnboarded };
		const {
			meta: { loadingProps: previousLoadingProps, loading: isLoadingInProgress, initialized },
		} = getState();

		// Only load again when the props this function depends on have changed
		if (forceReload !== true && isShallowEqual(loadingProps, previousLoadingProps)) {
			if (!getState().meta.projectIsNotOnboardedError) {
				return;
			}
		}

		if (forceReload !== true && !scheduledReload) {
			if (initialized) {
				return;
			}

			if (isLoadingInProgress) {
				setState({
					meta: {
						...getState().meta,
						scheduleFieldsReload: true,
					},
				});
				return;
			}
		}

		jpdProjectPageLoad.mark(PAGE_LOAD_MARK_LOAD_FIELDS_START);

		setState({
			meta: {
				...getState().meta,
				initialized: false,
				loading: true,
				error: undefined,
				loadingProps,
				projectIsNotOnboardedError: false,
			},
		});

		try {
			const fieldData = await fieldRemote.fetchAllFields();
			jpdProjectPageLoad.mark(PAGE_LOAD_MARK_LOAD_FIELDS_END);
			setState({
				meta: {
					...getState().meta,
					initialized: true,
					loading: false,
					scheduleFieldsReload: false,
					projectIsNotOnboardedError: false,
					error: undefined,
					loadingProps,
				},
				...fieldData,
			});
			detectAndHandleCyclicFormulaDependency(containerProps, fieldData.fields);
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			jpdProjectPageLoad.mark(PAGE_LOAD_MARK_LOAD_FIELDS_END);
			if (error instanceof RemoteFieldsNotInitializedError) {
				const scheduleFieldsReload = getState().meta.scheduleFieldsReload;
				if (!getState().meta.initialized || forceReload === true) {
					setState({
						meta: {
							...getState().meta,
							initialized: false,
							loading: false,
							scheduleFieldsReload: false,
							projectIsNotOnboardedError: true,
						},
					});

					if (scheduleFieldsReload) {
						setState({
							meta: {
								...getState().meta,
								loading: false,
								scheduleFieldsReload: false,
							},
						});
						dispatch(loadFields(false, true));
					}
				}

				// we return early here,
				// we want to try loading again since the project is still loading
				return;
			}

			const scheduleFieldsReload = getState().meta.scheduleFieldsReload;

			setState({
				meta: {
					...getState().meta,
					initialized: false,
					loading: false,
					error,
					scheduleFieldsReload: false,
					projectIsNotOnboardedError: false,
					loadingProps: undefined,
				},
				fields: {},
				fieldValueDecorations: {},
			});

			if (scheduleFieldsReload) {
				dispatch(loadFields(false, true));
				return;
			}

			if (error instanceof AccessForbiddenError) {
				return;
			}

			if (
				error instanceof SharedViewFieldsUnavailableError ||
				error instanceof SharedViewAuthenticationMissingError
			) {
				return;
			}

			if (isPermissionCheckError(error) && isSiteAdmin) {
				// shallow perms error for site admins as they still need to access setting page
				// even without project permissions
				return;
			}

			containerProps.onFieldLoadingFailed(error);
		}
	};
